import { Link } from "react-router";
import type { Market, MarketStatus } from "../api/types";
import { chips, countdown, outcomeLabel, pct, useNow } from "../lib/format";
import { Badge, cx } from "./ui";

// Outcome colors: YES/NO get the semantic pair; multi-outcome markets use a
// fixed palette that reads in light and dark.
const PALETTE = ["#4f46e5", "#0d9488", "#d97706", "#db2777", "#0284c7", "#65a30d", "#9333ea", "#dc2626", "#475569", "#ca8a04"];

export function outcomeColor(market: Pick<Market, "type">, i: number): string {
  if (market.type === "BINARY") return i === 0 ? "var(--yes)" : "var(--no)";
  return PALETTE[i % PALETTE.length]!;
}

export function StatusBadge({ status, settling }: { status: MarketStatus; settling?: boolean }) {
  if (settling) return <Badge tone="warn">Settling</Badge>;
  const map: Record<MarketStatus, [string, "neutral" | "accent" | "yes" | "no" | "warn"]> = {
    OPEN: ["Open", "accent"],
    VOTING: ["Voting", "warn"],
    NEEDS_ADMIN: ["Awaiting admin", "warn"],
    RESOLVED: ["Resolved · reversible", "yes"],
    FINAL: ["Final", "neutral"],
  };
  const [label, tone] = map[status];
  return <Badge tone={tone}>{label}</Badge>;
}

/** Headline probability: YES % for binary, the leader for multi. */
export function Headline({ market, size = "md" }: { market: Market; size?: "md" | "lg" }) {
  const big = size === "lg" ? "text-4xl" : "text-2xl";
  if (market.type === "BINARY") {
    return (
      <div className="flex items-baseline gap-1.5">
        <span className={cx(big, "font-semibold text-yes")}>{pct(market.probs[0] ?? 0)}</span>
        <span className="text-sm text-muted">chance</span>
      </div>
    );
  }
  const lead = market.probs.reduce((best, p, i) => (p > (market.probs[best] ?? 0) ? i : best), 0);
  return (
    <div className="flex items-baseline gap-1.5">
      <span className={cx(big, "font-semibold")}>{pct(market.probs[lead] ?? 0)}</span>
      <span className="truncate text-sm text-muted">{market.outcomes[lead]}</span>
    </div>
  );
}

/** Stacked bar of every outcome's probability. */
export function ProbBar({ market }: { market: Market }) {
  return (
    <div className="flex h-2 overflow-hidden rounded-full bg-surface-2" aria-hidden>
      {market.probs.map((p, i) => (
        <div key={i} style={{ width: `${p * 100}%`, background: outcomeColor(market, i) }} />
      ))}
    </div>
  );
}

export function MarketCard({ market }: { market: Market }) {
  const now = useNow(30_000);
  let timing = "";
  if (market.status === "OPEN") timing = `closes in ${countdown(market.closeAt, now)}`;
  else if (market.status === "VOTING") timing = `voting ends in ${countdown(market.voteEndsAt, now)}`;
  else if (market.result !== null) timing = `resolved ${outcomeLabel(market.outcomes, market.result)}`;
  return (
    <Link
      to={`/m/${market.id}`}
      className="block rounded-xl border border-line bg-surface p-4 transition hover:border-accent/50"
    >
      <div className="mb-2 flex items-center justify-between gap-2 text-xs text-muted">
        <span className="capitalize">{market.category}</span>
        <StatusBadge status={market.status} settling={market.settling} />
      </div>
      <h3 className="mb-3 font-medium leading-snug">{market.title}</h3>
      <div className="flex items-end justify-between gap-3">
        <Headline market={market} />
        <div className="text-right text-xs text-muted">
          <div>{timing}</div>
          <div>
            {market.traderCount} trader{market.traderCount === 1 ? "" : "s"} · {chips(market.totalWageredM)} wagered
          </div>
        </div>
      </div>
      <div className="mt-3">
        <ProbBar market={market} />
      </div>
    </Link>
  );
}
